/**
 * Flowlytics Pixel SDK - Device Intelligence Collector
 * Collects device and browser information
 */

class DeviceCollector {
  constructor(config) {
    this.config = config;
  }

  /**
   * Collect all device information
   */
  collect() {
    // Get WebGL info using a single shared context
    const webglInfo = this.getWebGLInfo();

    return {
      // Browser info
      user_agent: navigator.userAgent,
      language: navigator.language,
      languages: navigator.languages || [navigator.language],

      // Screen info
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      screen_available_width: window.screen.availWidth,
      screen_available_height: window.screen.availHeight,
      screen_color_depth: window.screen.colorDepth,
      screen_pixel_depth: window.screen.pixelDepth,

      // Viewport info
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,

      // Device info
      device_memory: navigator.deviceMemory || null,
      hardware_concurrency: navigator.hardwareConcurrency || null,
      max_touch_points: navigator.maxTouchPoints || 0,

      // Platform info
      platform: navigator.platform,
      vendor: navigator.vendor,

      // Connection info (if available)
      connection_type: this.getConnectionType(),
      connection_effective_type: this.getConnectionEffectiveType(),
      connection_downlink: this.getConnectionDownlink(),
      connection_rtt: this.getConnectionRTT(),

      // Timezone
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timezone_offset: new Date().getTimezoneOffset(),

      // Browser capabilities
      cookies_enabled: navigator.cookieEnabled,
      do_not_track: navigator.doNotTrack || 'unspecified',
      java_enabled: typeof navigator.javaEnabled === 'function' ? navigator.javaEnabled() : false,

      // Additional properties
      online: navigator.onLine,
      touch_support: this.getTouchSupport(),
      webgl_vendor: webglInfo.vendor,
      webgl_renderer: webglInfo.renderer
    };
  }

  /**
   * Get connection type
   */
  getConnectionType() {
    if (!navigator.connection) return null;
    return navigator.connection.type || null;
  }

  /**
   * Get effective connection type (4g, 3g, etc.)
   */
  getConnectionEffectiveType() {
    if (!navigator.connection) return null;
    return navigator.connection.effectiveType || null;
  }

  /**
   * Get connection downlink speed
   */
  getConnectionDownlink() {
    if (!navigator.connection) return null;
    return navigator.connection.downlink || null;
  }

  /**
   * Get connection RTT (round-trip time)
   */
  getConnectionRTT() {
    if (!navigator.connection) return null;
    return navigator.connection.rtt || null;
  }

  /**
   * Get touch support information
   */
  getTouchSupport() {
    return {
      max_touch_points: navigator.maxTouchPoints || 0,
      touch_event: 'ontouchstart' in window,
      touch_points: navigator.msMaxTouchPoints || 0
    };
  }

  /**
   * Get WebGL vendor and renderer using a single context
   */
  getWebGLInfo() {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      if (!gl) return { vendor: null, renderer: null };

      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      const result = {
        vendor: debugInfo ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : null,
        renderer: debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : null
      };

      // Explicitly release the WebGL context
      const loseContext = gl.getExtension('WEBGL_lose_context');
      if (loseContext) loseContext.loseContext();

      return result;
    } catch (e) {
      return { vendor: null, renderer: null };
    }
  }

  /**
   * Detect device type (mobile, tablet, desktop)
   */
  getDeviceType() {
    const ua = navigator.userAgent.toLowerCase();
    const isMobile = /mobile|android|iphone|ipod|blackberry|iemobile|opera mini/i.test(ua);
    const isTablet = /ipad|android(?!.*mobile)|tablet/i.test(ua);

    if (isTablet) return 'tablet';
    if (isMobile) return 'mobile';
    return 'desktop';
  }

  /**
   * Detect browser
   */
  getBrowser() {
    const ua = navigator.userAgent;

    if (ua.indexOf('Firefox') > -1) return 'Firefox';
    if (ua.indexOf('Opera') > -1 || ua.indexOf('OPR') > -1) return 'Opera';
    if (ua.indexOf('Trident') > -1) return 'IE';
    if (ua.indexOf('Edge') > -1) return 'Edge';
    if (ua.indexOf('Chrome') > -1) return 'Chrome';
    if (ua.indexOf('Safari') > -1) return 'Safari';

    return 'Unknown';
  }

  /**
   * Detect operating system
   */
  getOS() {
    const ua = navigator.userAgent;

    if (ua.indexOf('Win') > -1) return 'Windows';
    if (ua.indexOf('Mac') > -1) return 'MacOS';
    if (ua.indexOf('Linux') > -1) return 'Linux';
    if (ua.indexOf('Android') > -1) return 'Android';
    if (ua.indexOf('iOS') > -1 || ua.indexOf('iPhone') > -1 || ua.indexOf('iPad') > -1) return 'iOS';

    return 'Unknown';
  }

  /**
   * Get enriched device summary
   */
  getSummary() {
    return {
      device_type: this.getDeviceType(),
      browser: this.getBrowser(),
      os: this.getOS(),
      screen: `${window.screen.width}x${window.screen.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`
    };
  }
}

export default DeviceCollector;
